/**
 * AP-13: Hook zum Lesen des DataSourceMode pro Ref-Entität.
 *
 * Entscheidet, ob CRUD-Buttons (Neu / Bearbeiten / Löschen) aktiv sind:
 *   - INTERNAL  → aktiv, Pflege in Symphenia
 *   - MANUAL    → aktiv (Buchungsmaske ist im Freitext-Modus, Ref-Pflege
 *                 aber technisch erlaubt und unschädlich)
 *   - EXTERNAL_API → inaktiv, Daten gehören einem Fremdsystem
 *   - PROJECT_MGMT → inaktiv, Projekte kommen aus dem Projektmanagement (AP-35)
 *
 * Die drei TenantTimeConfig-Felder werden auf die fünf Ref-Entitäten gemappt:
 *   project | position | step → projectSource
 *   activity                  → activityTypeSource
 *   userRef                   → userSource
 */
import { useEffect, useState } from 'react';
import { timeConfigApi } from '@/features/time-tracking/api/time-tracking.api';
import { useTerminology } from '@/lib/useTerminology';
import type { RefEntityKind } from '../types/time-master-data.types';

export type DataSourceMode = 'INTERNAL' | 'MANUAL' | 'EXTERNAL_API' | 'PROJECT_MGMT';

export interface UseDataSourceModeResult {
  mode: DataSourceMode | null;
  isLoading: boolean;
  /** `true`, wenn CRUD (Create/Update/Delete) in der UI erlaubt sein soll. */
  canEdit: boolean;
  /** Benutzerfreundlicher Grund für `!canEdit` — sonst leer. */
  readOnlyReason: string;
}

const ENTITY_TO_FIELD: Record<RefEntityKind, 'projectSource' | 'activityTypeSource' | 'userSource'> = {
  project:  'projectSource',
  position: 'projectSource',
  step:     'projectSource',
  activity: 'activityTypeSource',
  userRef:  'userSource',
};

export function useDataSourceMode(kind: RefEntityKind): UseDataSourceModeResult {
  const terms = useTerminology();
  const [mode, setMode] = useState<DataSourceMode | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    timeConfigApi
      .get()
      .then(({ data }) => {
        if (cancelled) return;
        const field = ENTITY_TO_FIELD[kind];
        const value = (data as any)?.[field] as DataSourceMode | undefined;
        setMode(value ?? 'INTERNAL');
      })
      .catch(() => {
        if (!cancelled) setMode(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [kind]);

  const canEdit = mode != null && mode !== 'EXTERNAL_API' && mode !== 'PROJECT_MGMT';
  // Erst beim Anzeigen gebildet, nicht im Ladeeffekt — sonst stünde der Begriff
  // in dessen Abhängigkeiten (`.claude/rules/terminologie.md`). Ohne Artikel:
  // „das Projektmanagement", aber „die Auftragsverwaltung".
  const readOnlyReason =
    mode === 'EXTERNAL_API'
      ? 'Diese Daten kommen aus deinem anderen Programm. ' +
        'Umstellen kannst du das unter Einrichtung → Woher kommen die Daten?'
      : mode === 'PROJECT_MGMT'
        ? `Quelle: ${terms.management}. Gepflegt werden diese Daten dort, nicht hier.`
        : '';

  return { mode, isLoading, canEdit, readOnlyReason };
}
